import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { inr, ddmmyyyy } from "../lib/format";
import { Pill, PrimaryButton, VoiceButton } from "../components/common";
import { ChevronLeft, MapPin, Send, MessageCircle, CalendarDays, Loader2, Check, Sparkles, FileText } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600";

export default function PropertyDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [l, setL] = useState(null);
  const [faq, setFaq] = useState([]);
  const [q, setQ] = useState("");
  const [faqBusy, setFaqBusy] = useState(false);
  const [slot, setSlot] = useState("");
  const [booked, setBooked] = useState(false);

  useEffect(() => { api.get(`/listings/${id}`).then((r) => setL(r.data)).catch(() => {}); }, [id]);

  const ask = async (textArg) => {
    const text = (textArg ?? q).trim();
    if (!text) return;
    setQ(""); setFaq((m) => [...m, { role: "user", text, id: `u-${Date.now()}-${m.length}` }]); setFaqBusy(true);
    try { const { data } = await api.post(`/listings/${id}/faq`, { question: text, language: lang || "en" }); setFaq((m) => [...m, { role: "ai", text: data.answer, id: `a-${Date.now()}-${m.length}` }]); }
    catch { setFaq((m) => [...m, { role: "ai", text: "Please try again.", id: `a-${Date.now()}-${m.length}` }]); } finally { setFaqBusy(false); }
  };

  const book = async () => { if (!slot) return; await api.post(`/listings/${id}/book-visit`, { slot }); setBooked(true); };

  if (!l) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-terracotta" /></div>;
  const isOwner = user?.role === "owner";

  return (
    <div className="max-w-md md:max-w-4xl mx-auto min-h-screen bg-sand pb-28">
      <div className="relative">
        <img src={l.photos?.[0] || FALLBACK} alt="" className="w-full h-56 object-cover" onError={(e) => (e.target.src = FALLBACK)} />
        <button onClick={() => navigate(-1)} data-testid="detail-back-btn" className="absolute top-3 left-3 bg-white/90 rounded-full p-2"><ChevronLeft className="w-5 h-5" /></button>
      </div>
      {l.photos?.length > 1 && <div className="flex gap-2 px-4 py-3 overflow-x-auto no-scrollbar">{l.photos.slice(1).map((p) => <img key={p.slice(0, 48)} src={p} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />)}</div>}

      <div className="px-4 space-y-4">
        <div>
          <div className="flex items-start justify-between gap-2">
            <h1 className="font-head text-2xl font-bold text-slate-900">{l.title}</h1>
            <Pill tone="slate">{l.bhk}</Pill>
          </div>
          <div className="flex items-center gap-1 text-sm text-slate-400 mt-1"><MapPin className="w-4 h-4" /> {l.locality}, {l.city}, {l.state}</div>
          <div className="flex items-baseline gap-4 mt-3">
            <div className="font-mono font-bold text-terracotta text-2xl">{inr(l.rent)}<span className="text-slate-400 text-sm font-body">{t("per_month")}</span></div>
            <div className="text-sm text-slate-500">{t("deposit")} <span className="font-mono font-semibold text-slate-700">{inr(l.deposit)}</span></div>
          </div>
        </div>

        {l.warnings?.length > 0 && <div className="rounded-xl bg-amberw-light border border-amber-200 p-3 text-sm text-amberw">{l.warnings.join(" ")}</div>}

        {l.amenities?.length > 0 && <div className="flex flex-wrap gap-2">{l.amenities.map((a) => <Pill key={a} tone="trust">{a}</Pill>)}</div>}
        {l.house_rules?.length > 0 && <div className="flex flex-wrap gap-2">{l.house_rules.map((r) => <Pill key={r} tone="slate">{r}</Pill>)}</div>}
        {l.nearby && <div className="gs-card p-3 text-sm text-slate-600"><span className="font-semibold">{t("nearby")}: </span>{l.nearby}</div>}

        {isOwner ? (
          <PrimaryButton onClick={() => navigate(`/agreements/new?listing=${l.id}`)} data-testid="create-agreement-from-listing-btn"><FileText className="w-4 h-4 mr-2" />{t("gen_agreement")}</PrimaryButton>
        ) : (
          <>
            <div className="gs-card p-4">
              <div className="flex items-center gap-2 font-head font-semibold text-slate-800 mb-3"><CalendarDays className="w-5 h-5 text-terracotta" /> {t("book_visit")}</div>
              {booked ? <div className="flex items-center gap-2 text-trust font-semibold text-sm"><Check className="w-4 h-4" /> {t("book_visit")} ✓ {slot}</div> : (
                <>
                  <div className="grid grid-cols-2 gap-2 mb-3">{(l.visit_slots || []).map((s) => (
                    <button key={s} onClick={() => setSlot(s)} data-testid={`slot-${s}`} className={`gs-tap rounded-xl border text-sm ${slot === s ? "border-terracotta bg-terracotta-light text-terracotta-dark" : "border-slate-200 text-slate-600"}`}>{s}</button>
                  ))}</div>
                  <PrimaryButton onClick={book} disabled={!slot} data-testid="book-visit-btn">{t("book_visit")}</PrimaryButton>
                </>
              )}
            </div>

            <div className="gs-card p-4">
              <div className="flex items-center gap-2 font-head font-semibold text-slate-800 mb-1"><MessageCircle className="w-5 h-5 text-terracotta" /> {t("ask_about_home")}</div>
              <div className="flex items-center gap-1.5 text-xs text-terracotta mb-3"><Sparkles className="w-3 h-3" /> Auto-answered by Sathi AI</div>
              <div className="space-y-2 max-h-64 overflow-y-auto no-scrollbar mb-3" data-testid="faq-messages">
                {faq.length === 0 && <p className="text-xs text-slate-400">Ask about rent, deposit, availability, nearby metro/school…</p>}
                {faq.map((m) => <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}><div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-terracotta text-white" : "bg-slate-100 text-slate-700"}`}>{m.text}</div></div>)}
                {faqBusy && <Loader2 className="w-4 h-4 animate-spin text-terracotta" />}
              </div>
              <div className="flex items-center gap-2">
                <VoiceButton onResult={ask} testId="faq-voice-btn" />
                <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} data-testid="faq-input" className="flex-1 gs-tap rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-terracotta" placeholder={t("type_message")} />
                <button onClick={() => ask()} data-testid="faq-send-btn" className="gs-tap w-11 flex items-center justify-center rounded-xl bg-terracotta text-white"><Send className="w-4 h-4" /></button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
